import { z } from 'zod';
import {
  ApiKeyRequestOutcomeSchema,
  ApiKeyRequestReplayAttemptSchema,
  ApiKeyRequestReplayBlockedReasonSchema,
} from './api-key.schemas';
import { IncludeTestWorkspacesQuerySchema } from './test-workspaces.schemas';

// Operator read surface over the universal `api_call_log` store (IMM-457.2).
// Read-only, gated by the `backoffice:identity:api-call-read` permission. A
// SEPARATE surface from the per-key trail in `api-key.schemas.ts`: this reads
// the new store that captures EVERY inbound `/v1` call — API-key, member
// session, impersonation, anonymous and malformed alike — and is addressed by
// the entry's own id, never scoped to a key. List items omit both bodies (size
// + PII minimisation); only the detail endpoint returns them.

// The credential class an inbound request presented, as resolved by the auth
// path — NOT inferred from a missing key id. Kept in sync by hand with the
// backend `CredentialClass` domain type (the contract package cannot import
// backend code), the same way `ApiKeyRequestOutcomeSchema` is.
export const ApiCallCredentialClassSchema = z.enum([
  'api-key',
  'member-session',
  'impersonation',
  'backoffice-credential',
  'operator-session',
  // An MCP-capable `immo_` key calling the workspace MCP endpoint (IMM-601.2).
  // Same credential kind as `api-key`, split so operator surfaces can tell MCP
  // tool traffic from plain HTTP API traffic at a glance.
  'workspace-mcp',
  // An OAuth access token calling the workspace MCP endpoint (IMM-601.8.1) — a
  // connected client acting for a member, with NO `api_keys` row behind it. Its
  // own class so operator surfaces can tell third-party client traffic from the
  // member's own key traffic, and so a refused OAuth bearer is never logged as a
  // failed browser session.
  'workspace-mcp-oauth',
  // The valuation agent's `vag_` token (IMM-1402): a machine credential bound to no workspace and no member.
  'valuation-agent',
  'anonymous',
  'malformed',
]);
export type ApiCallCredentialClass = z.infer<typeof ApiCallCredentialClassSchema>;

// Why the credential was refused; null when authentication succeeded. Kept in
// sync by hand with the backend `ApiCallRejectionReason` domain type.
export const ApiCallRejectionReasonSchema = z.enum([
  'credential-absent',
  'credential-malformed',
  'api-key-unknown',
  'api-key-member-inactive',
  // A `svc_` key is active but the operator it is bound to has been deactivated
  // (IMM-556.2). Distinct from `api-key-member-inactive` so an operator reading
  // the log sees an offboarding killing its integrations rather than a bad
  // credential; the wire response is the same flat 401 either way.
  'service-key-admin-inactive',
  'session-expired',
  'session-invalid',
  'impersonation-invalid',
  'workspace-mismatch',
  'backoffice-credential-invalid',
  'backoffice-credential-wrong-route',
  'backoffice-surface-disabled',
  // The platform-wide CUSTOMER MCP switch is off (IMM-601.6). The wire response
  // is the same flat 401 an invalid credential gets — this reason exists so an
  // operator reading the log sees a containment action rather than a wave of
  // bad customer keys.
  'workspace-mcp-surface-disabled',
  // A `tools/call` named a tool the platform has not exposed (IMM-625.1). The
  // client reads the SAME `-32602` unknown-tool refusal a name that never
  // existed gets — so the exposed set cannot be mapped by probing — and this
  // reason is the only place the difference survives.
  'tool-not-exposed',
  // An OAuth bearer on `/v1/mcp` failed validation (IMM-601.8.1): bad signature,
  // expired, a foreign issuer, an audience naming another resource, or a grant
  // row that is absent or revoked. Deliberately ONE reason for all of them — the
  // surface must not be probeable — and the wire response is the same flat 401
  // an unknown key gets.
  'oauth-token-invalid',
  // A route accepting ONLY a workspace `immo_` key was reached with something
  // else — a browser session, an `svc_` admin key, or nothing (IMM-751.2.2).
  // Those routes carry no workspace in their path, so they cannot fall back to
  // a membership check the way every other customer route does.
  'workspace-key-required',
  // A valid workspace key reached the handoff exchange but is configured for no
  // linked application (IMM-751.2.2). The credential is fine and the deployment
  // is half-configured — the key was issued and its id has not been added to
  // `LINKED_APPLICATIONS` yet. Its own reason because the fix is an operator's,
  // not the caller's; the wire response is the same flat 401 either way.
  'app-handoff-key-unbound',
  // A `vag_` bearer that is unknown or revoked (IMM-1402). One reason for both, as for `svc_`.
  'valuation-agent-token-invalid',
]);
export type ApiCallRejectionReason = z.infer<typeof ApiCallRejectionReasonSchema>;

// Whether the bodies were captured, and why not when they were not. Governs the
// two body columns only; metadata is always present.
export const ApiCallBodiesStoredSchema = z.enum([
  'stored',
  'stored-request-only',
  'suppressed-auth-route',
  'suppressed-unmatched-anonymous',
]);
export type ApiCallBodiesStored = z.infer<typeof ApiCallBodiesStoredSchema>;

// The HTTP verb exactly as it arrived — free-form on PURPOSE, and deliberately
// NOT the five-verb `ApiKeyRequestMethodSchema` of the per-key trail. That trail
// only ever sees API-key traffic on matched routes; this store captures EVERY
// inbound `/v1` call, so it also holds the verbs the app never routes: browser
// CORS preflights (`OPTIONS`), `HEAD` probes, and whatever else a scanner sends
// that Node's parser accepts. The domain type and the `text` column are both
// unconstrained; narrowing here made the list endpoint 500 on serialisation the
// moment a preflight was logged, so the contract now matches what is stored.
export const ApiCallMethodSchema = z.string().min(1);
export type ApiCallMethod = z.infer<typeof ApiCallMethodSchema>;

// One row on the global call log — every captured metadata field plus the
// LEFT-joined friendly labels, and NEVER the bodies. The captured ids are
// always retained (the log is historical); `workspaceName` / `apiKeyName` /
// `memberEmail` are null when the referenced row was since deleted, or never
// resolved (an anonymous / malformed / auth-failed call). `operatingAdminEmail`
// is resolved through the admin lookup port; the captured id survives deletion.
export const ApiCallListItemSchema = z.object({
  id: z.uuid(),
  requestId: z.string(),
  credentialClass: ApiCallCredentialClassSchema,
  rejectionReason: ApiCallRejectionReasonSchema.nullable(),
  bodiesStored: ApiCallBodiesStoredSchema,
  workspaceId: z.uuid().nullable(),
  workspaceName: z.string().nullable(),
  // Whether the call's workspace is a testing environment (IMM-593.2). Always
  // false for a call that never resolved to a workspace.
  isTest: z.boolean(),
  apiKeyId: z.uuid().nullable(),
  apiKeyName: z.string().nullable(),
  memberId: z.uuid().nullable(),
  memberEmail: z.string().nullable(),
  operatingAdminId: z.uuid().nullable(),
  operatingAdminEmail: z.string().nullable(),
  method: ApiCallMethodSchema,
  endpoint: z.string(),
  // The values the `endpoint` template's `:params` matched, e.g.
  // `{ organizationId: '018f…', id: '019a…' }`. The template alone cannot say
  // WHICH record a call touched, so every row on a busy endpoint reads the same.
  // Null when the route takes no params, or when the call hit an auth route
  // (where a path param would be credential material, as the bodies are).
  pathParams: z.record(z.string(), z.string()).nullable(),
  permission: z.string().nullable(),
  statusCode: z.number().int(),
  outcome: ApiKeyRequestOutcomeSchema,
  durationMs: z.number().int(),
  clientIp: z.string().nullable(),
  userAgent: z.string().nullable(),
  // The JSON-RPC method a `/v1/mcp` request carried (IMM-690.1). `/v1/mcp` is
  // one endpoint for every MCP operation, so `endpoint` alone distinguishes
  // nothing — this is the dispatch key. Read from the BODY, never the
  // `Mcp-Method` header (optional on every era since IMM-699.1). Null for
  // every non-MCP call.
  mcpMethod: z.string().nullable(),
  // The MCP tool a `tools/call` named (stored since IMM-601.2, surfaced here).
  // Null for MCP requests that call no tool and for every non-MCP call.
  toolName: z.string().nullable(),
  occurredAt: z.iso.datetime(),
  // Rides the LIST item because recovery is worked as a queue: an operator going
  // through a backlog needs the last-run status per row without opening each one.
  // Null when never replayed. The fields above always stay the ORIGINAL call.
  lastReplay: ApiKeyRequestReplayAttemptSchema.nullable(),
});
export type ApiCallListItem = z.infer<typeof ApiCallListItemSchema>;

// Detail = list item plus the two stored bodies. Bodies are captured as text
// (JSON payloads, already credential-stripped and 64 KB-capped by IMM-457.1);
// null when absent (a body-less response) or when `bodiesStored` is a
// suppression — the UI reads `bodiesStored` to tell "none sent" from "refused
// to store", so an empty panel never reads as a bug.
export const ApiCallDetailSchema = ApiCallListItemSchema.extend({
  requestBody: z.string().nullable(),
  responseBody: z.string().nullable(),
  // Whether this entry can be re-submitted (IMM-458, generalised onto the call
  // log by IMM-494.3), resolved server-side so the admin app never re-implements
  // the eligibility rule. `false` always comes with a reason; `true` always with
  // null. Only the lead push is eligible today — every other endpoint reports
  // ENDPOINT_NOT_REPLAYABLE, which is now a policy allow-list rather than a
  // capability limit.
  replayable: z.boolean(),
  replayBlockedReason: ApiKeyRequestReplayBlockedReasonSchema.nullable(),
  // Every replay attempt against this call, oldest first; empty when never
  // replayed. `statusCode` / `outcome` / `responseBody` above always describe
  // the ORIGINAL call and are never rewritten — the recovery lives here.
  replays: z.array(ApiKeyRequestReplayAttemptSchema),
});
export type ApiCallDetail = z.infer<typeof ApiCallDetailSchema>;

// Structured filters — all optional, AND-combined. Omitting every filter
// returns everything, which is the point of the surface. An unknown
// `workspaceId` yields an empty page, never a 404 (no existence check). `from` /
// `to` bound `occurredAt`; `limit` defaults to 50 (1–100); `cursor` is the
// opaque keyset token echoed from a previous page's `nextCursor`.
export const ListApiCallsQuerySchema = z
  .object({
    workspaceId: z.uuid().optional(),
    // The NULL branch an id filter cannot express: `eq(column, value)` is
    // NULL-false, so a call whose workspace or key never resolved matches no id
    // filter at all. These select exactly those rows (IMM-494.3, BL-001).
    workspaceUnresolved: z.coerce.boolean().optional(),
    apiKeyId: z.uuid().optional(),
    apiKeyUnresolved: z.coerce.boolean().optional(),
    credentialClass: ApiCallCredentialClassSchema.optional(),
    outcome: ApiKeyRequestOutcomeSchema.optional(),
    // Exact (case-insensitive) match on the captured verb — omit to get every
    // method, which is the default. Free-form rather than an enum for the same
    // reason `ApiCallMethodSchema` is: the store holds verbs the app never
    // routes, so a fixed list would leave the noisiest of them (`OPTIONS`)
    // unfilterable. Bounded in length because it is an operator-typed URL
    // parameter.
    method: z.string().min(1).max(32).optional(),
    endpoint: z.string().min(1).max(200).optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    cursor: z.string().optional(),
    // The operator app's test-workspace filter (IMM-593.2): omitted / false
    // hides calls resolved to a test workspace; unresolved calls always show.
    ...IncludeTestWorkspacesQuerySchema.shape,
  })
  // Pinning a dimension to an id and to "unresolved" at once asks for rows that
  // are both, which is empty by construction. Only a broken client can send it,
  // so it is a malformed request rather than a business rule — the platform's
  // zod envelope reports it and no error code is minted for it.
  .refine((query) => !(query.workspaceId !== undefined && query.workspaceUnresolved === true), {
    message: 'workspaceId and workspaceUnresolved are mutually exclusive',
    path: ['workspaceUnresolved'],
  })
  .refine((query) => !(query.apiKeyId !== undefined && query.apiKeyUnresolved === true), {
    message: 'apiKeyId and apiKeyUnresolved are mutually exclusive',
    path: ['apiKeyUnresolved'],
  });
export type ListApiCallsQuery = z.infer<typeof ListApiCallsQuerySchema>;

export const ListApiCallsResponseSchema = z.object({
  data: z.array(ApiCallListItemSchema),
  // Pass back as `?cursor=` for the next page; null when the list is exhausted.
  nextCursor: z.string().nullable(),
});
export type ListApiCallsResponse = z.infer<typeof ListApiCallsResponseSchema>;

// The entry is addressed by its OWN id, with NO key scoping — the deliberate
// fix for the nested per-key detail that 404'd on keyless rows (IMM-441 /
// IMM-446). `requireAdmin` + the read permission is the gate.
export const GetApiCallPathParamsSchema = z.object({
  callId: z.uuid(),
});

export const ReplayApiCallPathParamsSchema = GetApiCallPathParamsSchema;

export const GetApiCallResponseSchema = ApiCallDetailSchema;
export type GetApiCallResponse = z.infer<typeof GetApiCallResponseSchema>;
